import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Button, ListContextProvider, useList } from "react-admin";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import { SectionTitle } from "features/SectionTitle";
import ItemFormDialog from "../dialogs/ItemFormDialog";
import { useCurriculum } from "../CurriculumContext";
import { CollectionContext } from "./CollectionContext";
import { useCollectionMutations } from "./useCollectionMutations";
import { useIncrementalRows } from "./useIncrementalRows";
import LoadMoreButton from "./LoadMoreButton";
import { toRows } from "./rows";

// Both layouts render through this; they differ only in how sections are arranged around it.
const CollectionSection = ({ collection }) => {
  const curriculum = useCurriculum();
  const mutations = useCollectionMutations(curriculum, collection);
  const { items, saving, addItem } = mutations;

  // rowIndex is stamped before slicing, so a row still addresses the right item once revealed.
  const rows = useMemo(() => toRows(collection.key, items), [collection.key, items]);
  const { visibleRows, remaining, isPending, loadMore, showAll } = useIncrementalRows(rows);
  const listContext = useList({ data: visibleRows });
  const summary = collection.summary?.(items) ?? [];

  // Appended rows would otherwise stay hidden behind the load-more button.
  const addAndReveal = async data => {
    await addItem(data);
    showAll();
  };

  return (
    <CollectionContext.Provider value={mutations}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <SectionTitle
            title={collection.label}
            paddingY={0}
          />
          {summary.map(entry => (
            <Typography
              key={entry.label}
              variant="body2"
              color="text.secondary"
            >
              {entry.label}: <strong>{entry.value}</strong>
            </Typography>
          ))}
        </Box>

        <SecuredComponent permission={permissions.canUpdateCurriculums}>
          <ItemFormDialog
            title={`Добавяне · ${collection.singular}`}
            FormFields={collection.FormFields}
            onSave={addAndReveal}
            dialogSize={collection.dialogSize}
            transform={collection.transform}
            triggerButton={
              <Button
                label="Добави"
                variant="contained"
                startIcon={<AddIcon />}
                disabled={saving}
              />
            }
          />
        </SecuredComponent>
      </Box>

      {items.length ? (
        <>
          <ListContextProvider value={listContext}>
            <collection.Grid collection={collection} />
          </ListContextProvider>
          <LoadMoreButton
            remaining={remaining}
            isPending={isPending}
            onLoadMore={loadMore}
            onShowAll={showAll}
          />
        </>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", py: 3 }}
        >
          Няма добавени записи.
        </Typography>
      )}
    </CollectionContext.Provider>
  );
};

export default CollectionSection;
